import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Copy, ExternalLink, Images, Loader2, RefreshCw, Save, Send } from 'lucide-react'

import { hasPermission } from '../../../app/access'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { Select } from '../../../components/ui/Select'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useAuthStore } from '../../../stores/authStore'
import {
  MediaRibbon,
  type CardMediaUploadResult,
  type RibbonOrigin,
} from '../../marketplace-catalog/components/media-ribbon/MediaRibbon'
import { affectedCardsText, deriveVisualMeta } from '../../marketplace-catalog/components/media-ribbon/model'
import { productsV2API, type ProductFilterOptions } from '../api'
import type { ProductPresenceEntry, ProductVisualPhoto, ProductVisualsSaveResult } from '../types'

interface ProductMediaManagerProps {
  article: string
  /** If omitted (site-card entry point), the manager resolves it through Product:READ. */
  origin?: string | null
  onChanged?: () => void
  /** Номер тематики товара: подсказка в охвате «Вся тематика в кабинете». */
  themeNumber?: number | null
  /** Открыть «Переотправить фото…» (окно товара); без него кнопки в итоге сохранения нет. */
  onResendPhotos?: () => void
  /** Сколько кабинетов с визуалами нашлось: окно показывает запасную сетку, если их нет. */
  onLoaded?: (summary: { cabinets: number }) => void
  /**
   * У товара есть визуалы вне кабинетов ПИМ (старый источник, импорт с площадки), а в
   * кабинетах пусто: окно показывает их само, а менеджер вместо пустых вкладок кабинетов
   * сворачивается в кнопку «Разложить по кабинету…».
   */
  collapseWhenEmpty?: boolean
}

interface SessionUpload {
  file: File
  sha256?: string | null
}

const LAST_PHOTO_TEXT =
  'Последнее фото удалить нельзя — карточка осталась бы без картинки. Загрузите новое, оно заменит это.'

const PIPELINE_WARNING =
  'Ручной порядок сохранится, но следующая перегенерация визуалов по воронке заменит его. ' +
  'Карточки на площадках обновятся через журнал правок по кнопке «Переотправить фото».'

const SCOPES = [
  { key: 'cabinet', label: 'Товар в кабинете' },
  { key: 'theme', label: 'Вся тематика в кабинете' },
]
const THEME_FUNNELS_HREF = '/content-funnels?tab=series-theme'

const errorText = (error: any) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'Не удалось выполнить запрос'

const fileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })

const fileFormat = (file: File): 'jpg' | 'jpeg' | 'png' | 'webp' => {
  const value = (file.name.split('.').pop() || 'jpg').toLowerCase()
  return value === 'jpeg' || value === 'png' || value === 'webp' ? value : 'jpg'
}

/** Кабинеты, где визуалы действительно есть (пустой кабинет не считается). */
const countFilled = (byCabinet: Record<string, ProductVisualPhoto[]>): number =>
  Object.values(byCabinet).filter((list) => list.length > 0).length

const normalizeCabinetPhotos = (photos: ProductVisualPhoto[]): ProductVisualPhoto[] =>
  photos.map((photo, index) => ({
    ...photo,
    origin: photo.origin || 'funnel',
    is_primary: index === 0,
    part_index: index,
  }))

/** Кадр воронки серий отличается от обычного кадра воронки по папке `fs_slide_N`. */
const ribbonOrigin = (photo: ProductVisualPhoto): RibbonOrigin => {
  if (photo.origin === 'manual' || photo.origin === 'mp_import') return photo.origin
  return deriveVisualMeta(photo.url).origin === 'series' ? 'series' : 'funnel'
}

export function ProductMediaManager({
  article,
  origin,
  onChanged,
  themeNumber,
  onResendPhotos,
  onLoaded,
  collapseWhenEmpty = false,
}: ProductMediaManagerProps) {
  const permissions = useAuthStore((state) => state.user?.permissions || [])
  const canUpdate = permissions.includes('Product:UPDATE')
  const canRebuildSite = permissions.includes('Site:UPDATE')
  const canOpenFunnels = hasPermission('ContentFunnel:READ', permissions)
  const [resolvedOrigin, setResolvedOrigin] = useState<string | null | undefined>(origin)
  const [photosByCabinet, setPhotosByCabinet] = useState<Record<string, ProductVisualPhoto[]>>({})
  /** Набор кабинета как он сохранён: с ним лента сравнивает правку. */
  const [savedByCabinet, setSavedByCabinet] = useState<Record<string, ProductVisualPhoto[]>>({})
  const [presence, setPresence] = useState<ProductPresenceEntry[] | null>(null)
  const [sellers, setSellers] = useState<ProductFilterOptions['sellers']>([])
  const [activeCabinet, setActiveCabinet] = useState('site_pt')
  const [scope, setScope] = useState('cabinet')
  const [copyTarget, setCopyTarget] = useState('')
  const [dirtyCabinets, setDirtyCabinets] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copying, setCopying] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [savedSite, setSavedSite] = useState(false)
  /** Пользователь раскрыл ручную раскладку по кабинетам при свёрнутом пустом менеджере. */
  const [placing, setPlacing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [journaledNotice, setJournaledNotice] = useState<string | null>(null)
  const sessionUploads = useRef(new Map<string, SessionUpload>())
  const onLoadedRef = useRef(onLoaded)
  onLoadedRef.current = onLoaded

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setSavedSite(false)
    setPlacing(false)
    sessionUploads.current.clear()
    const originRequest = origin === undefined ? productsV2API.getProduct(article) : Promise.resolve(null)
    // Присутствие нужно только для фразы «затронет N карточек»: его отказ окно не роняет.
    const presenceRequest = Promise.resolve()
      .then(() => productsV2API.getPresence(article))
      .then((data) => (Array.isArray(data?.entries) ? data.entries : null))
      .catch(() => null)
    void Promise.all([
      productsV2API.getProductVisuals(article),
      productsV2API.getFilterOptions().catch(() => ({ sellers: [] }) as any),
      originRequest,
      presenceRequest,
    ])
      .then(([visuals, options, product, entries]) => {
        if (!alive) return
        const next: Record<string, ProductVisualPhoto[]> = {}
        for (const cabinet of visuals.cabinets || []) {
          next[cabinet.cabinet] = normalizeCabinetPhotos(cabinet.photos || [])
        }
        setPhotosByCabinet(next)
        setSavedByCabinet(next)
        setPresence(entries)
        setSellers(options.sellers || [])
        setResolvedOrigin(origin === undefined ? product?.design?.origin ?? null : origin)
        const first = next.site_pt ? 'site_pt' : Object.keys(next)[0] || 'site_pt'
        setActiveCabinet(first)
        setScope('cabinet')
        setDirtyCabinets(new Set())
        onLoadedRef.current?.({ cabinets: countFilled(next) })
      })
      .catch((requestError) => alive && setError(errorText(requestError)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [article, origin])

  const cabinetOptions = useMemo(() => {
    const values = new Map<string, string>([['site_pt', 'Сайт']])
    sellers.forEach((seller) => {
      if (seller.cabinet_code) {
        values.set(
          seller.cabinet_code,
          `${seller.name}${seller.marketplace ? ` · ${seller.marketplace.toUpperCase()}` : ''}`,
        )
      }
    })
    Object.keys(photosByCabinet).forEach((cabinet) => {
      if (!values.has(cabinet)) values.set(cabinet, cabinet)
    })
    return [...values].map(([value, label]) => ({ value, label }))
  }, [photosByCabinet, sellers])

  const targetOptions = cabinetOptions.filter((option) => option.value !== activeCabinet)
  useEffect(() => {
    if (!targetOptions.some((option) => option.value === copyTarget)) {
      setCopyTarget(targetOptions[0]?.value || '')
    }
  }, [activeCabinet, copyTarget, targetOptions])

  const photos = photosByCabinet[activeCabinet] || []
  const urls = photos.map((photo) => photo.url)
  const baselineUrls = useMemo(
    () => (savedByCabinet[activeCabinet] || []).map((photo) => photo.url),
    [activeCabinet, savedByCabinet],
  )
  const markChanged = (cabinet: string, next: ProductVisualPhoto[]) => {
    setPhotosByCabinet((current) => ({ ...current, [cabinet]: normalizeCabinetPhotos(next) }))
    setDirtyCabinets((current) => {
      const dirty = new Set(current)
      // Вернули исходный набор — кабинет снова «не изменён».
      const same =
        next.length === (savedByCabinet[cabinet] || []).length &&
        next.every((photo, index) => photo.url === (savedByCabinet[cabinet] || [])[index]?.url)
      if (same) dirty.delete(cabinet)
      else dirty.add(cabinet)
      return dirty
    })
    setSavedSite(false)
    setNotice(null)
    setJournaledNotice(null)
  }

  const changeUrls = (nextUrls: string[]) => {
    const currentByUrl = new Map(photos.map((photo) => [photo.url, photo]))
    markChanged(
      activeCabinet,
      nextUrls.map((url) => {
        const current = currentByUrl.get(url)
        const session = sessionUploads.current.get(url)
        return current || { url, sha256: session?.sha256 || null, origin: 'manual' }
      }),
    )
  }

  const uploaded = (result: CardMediaUploadResult) => {
    sessionUploads.current.set(result.url, { file: result.file, sha256: result.sha256 })
  }

  // Бэкенд принимает набор товара с макетом только при явном согласии клиента.
  const isPipeline = resolvedOrigin !== 'external'

  const cardsOfCabinet = (cabinet: string): number | null => {
    if (!presence) return null
    if (cabinet === 'site_pt') {
      return presence.filter(
        (entry) => entry.marketplace === 'site' && (entry.state !== 'none' || entry.mirror || entry.pim_card),
      ).length
    }
    return presence.filter((entry) => entry.cabinet_code === cabinet && (entry.pim_card || entry.mirror)).length
  }
  const affectedCount = cardsOfCabinet(activeCabinet)
  const activeLabel = cabinetOptions.find((option) => option.value === activeCabinet)?.label ?? activeCabinet

  const saveCabinet = async (cabinet: string, set: ProductVisualPhoto[]): Promise<ProductVisualsSaveResult> =>
    isPipeline
      ? productsV2API.setProductVisuals(article, cabinet, set, { allowPipelineProduct: true })
      : productsV2API.setProductVisuals(article, cabinet, set)

  const save = async () => {
    if (!photos.length) {
      setError('Нельзя сохранить пустой набор фотографий')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const result = await saveCabinet(activeCabinet, photos)
      setDirtyCabinets((current) => {
        const next = new Set(current)
        next.delete(activeCabinet)
        return next
      })
      setSavedByCabinet((current) => ({ ...current, [activeCabinet]: photos }))
      onLoadedRef.current?.({ cabinets: countFilled({ ...savedByCabinet, [activeCabinet]: photos }) })
      setSavedSite(activeCabinet === 'site_pt')
      setNotice('Набор сохранён. Публичные файлы не удалялись; изменена только привязка к товару')
      setJournaledNotice(
        result?.journaled
          ? `Правка записана в журнал: ${affectedCardsText(result.affected_cards ?? 1)} кабинета обновится по кнопке «Переотправить фото»`
          : null,
      )
      onChanged?.()
    } catch (saveError) {
      setError(errorText(saveError))
    } finally {
      setSaving(false)
    }
  }

  const requestSave = () => {
    if (isPipeline) setConfirmOpen(true)
    else void save()
  }

  const copy = async () => {
    if (!copyTarget || !photos.length) return
    const missingBytes = photos.some((photo) => !sessionUploads.current.has(photo.url))
    if (missingBytes) return
    setCopying(true)
    setError(null)
    try {
      const copied: ProductVisualPhoto[] = []
      for (const photo of photos) {
        const session = sessionUploads.current.get(photo.url)!
        const image = await fileAsDataUrl(session.file)
        const result = await productsV2API.uploadManualVisual({
          article,
          cabinet: copyTarget,
          image,
          format: fileFormat(session.file),
        })
        sessionUploads.current.set(result.url, { file: session.file, sha256: result.sha256 })
        copied.push({ url: result.url, sha256: result.sha256 || null, origin: 'manual' })
      }
      await saveCabinet(copyTarget, copied)
      const normalized = normalizeCabinetPhotos(copied)
      setPhotosByCabinet((current) => ({ ...current, [copyTarget]: normalized }))
      setSavedByCabinet((current) => ({ ...current, [copyTarget]: normalized }))
      setNotice(`Набор скопирован в кабинет ${copyTarget}`)
      onChanged?.()
    } catch (copyError) {
      setError(errorText(copyError))
    } finally {
      setCopying(false)
    }
  }

  const rebuildSite = async () => {
    setRebuilding(true)
    setError(null)
    try {
      await productsV2API.rebuildProductSiteCard(article)
      setSavedSite(false)
      setNotice('Карточка сайта пересобрана')
      onChanged?.()
    } catch (rebuildError) {
      setError(errorText(rebuildError))
    } finally {
      setRebuilding(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 aria-label="Загрузка фотографий" className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (collapseWhenEmpty && !error && !placing && countFilled(savedByCabinet) === 0) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          icon={<Images className="h-4 w-4" />}
          disabled={!canUpdate}
          title={!canUpdate ? 'Нет права на изменение товаров' : undefined}
          onClick={() => setPlacing(true)}
        >
          Разложить по кабинету…
        </Button>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          Загрузить фото в кабинет вручную: выбор кабинета и порядок кадров.
        </span>
      </div>
    )
  }

  /**
   * Набор правится у любого товара — и у созданного вручную (без макета), и у
   * товара с макетом (решение владельца 11.10.2026). У второго ручной порядок
   * переживёт только до следующей перегенерации визуалов по воронке: ему перед
   * сохранением показывается предупреждение, а серверу уходит явный флаг.
   * Прежняя версия (до 30.08.2026) здесь вообще не показывала набор такого
   * товара, потом показывала только для чтения.
   */
  const canEdit = canUpdate
  const copyUnavailable = photos.some((photo) => !sessionUploads.current.has(photo.url))
  const marketplace = sellers.find((seller) => seller.cabinet_code === activeCabinet)?.marketplace
  // Геометрия показа: вертикальные фото ВБ, Ozon и сайта, квадратное превью Маркета.
  const aspect = marketplace === 'ym' || marketplace === 'yandex_market' ? '1:1' : '3:4'
  const photoByUrl = new Map(photos.map((photo) => [photo.url, photo]))

  const themeContent = (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200">
      <p>
        Набор всей тематики{themeNumber != null ? ` ${themeNumber}` : ''} в кабинете{' '}
        <b>{activeLabel}</b> меняется в разделе «Контент-воронки» → «По тематикам»: запуск серии пересобирает
        визуалы сразу у всех товаров тематики. Отсюда тематику править нельзя — здесь правится только этот товар.
      </p>
      {canOpenFunnels ? (
        <Link
          to={THEME_FUNNELS_HREF}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Открыть «Контент-воронки» → «По тематикам»
        </Link>
      ) : (
        <p className="text-xs text-gray-500 dark:text-zinc-400">Раздел доступен с правом ContentFunnel:READ.</p>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      {isPipeline && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
          Визуалы этого товара делает конвейер по макету. Порядок можно поменять вручную, но следующая
          перегенерация визуалов по воронке заменит его.
        </div>
      )}
      {!canUpdate && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Нет права на изменение товаров — фотографии доступны только для просмотра.
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300">
          <p>{notice}</p>
          {journaledNotice && (
            <p className="mt-1 flex flex-wrap items-center gap-2">
              <span>{journaledNotice}</span>
              {onResendPhotos && (
                <Button size="sm" variant="secondary" icon={<Send className="h-3.5 w-3.5" />} onClick={onResendPhotos}>
                  Переотправить фото…
                </Button>
              )}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Кабинеты фотографий">
        {cabinetOptions.map((option) => {
          const count = photosByCabinet[option.value]?.length || 0
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={activeCabinet === option.value}
              onClick={() => setActiveCabinet(option.value)}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                activeCabinet === option.value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-gray-300 text-gray-600 dark:border-zinc-700 dark:text-zinc-300'
              }`}
            >
              {option.label} ({count}){dirtyCabinets.has(option.value) ? ' · изменён' : ''}
            </button>
          )
        })}
      </div>

      <MediaRibbon
        key={activeCabinet}
        article={article}
        cabinet={activeCabinet}
        urls={urls}
        onChange={changeUrls}
        baselineUrls={baselineUrls}
        aspect={aspect}
        maxCount={30}
        disabled={!canEdit}
        uploadEndpoint="/products/visuals/upload"
        onUploaded={uploaded}
        canRemove={() => canEdit && photos.length > 1}
        removeDisabledReason={LAST_PHOTO_TEXT}
        emptyText="Фотографий в этом кабинете пока нет"
        scopes={SCOPES}
        scope={scope}
        onScopeChange={setScope}
        scopeContent={scope === 'theme' ? themeContent : undefined}
        affectedCards={affectedCount ?? undefined}
        affectedText={affectedCount === 0 ? '0 карточек — изменится только набор в ПИМ' : undefined}
        renderImage={(src, alt, className) => (
          <AuthorizedMediaImage src={src} alt={alt} className={className} loading="lazy" />
        )}
        originOf={(url) => {
          const item = photoByUrl.get(url)
          return item ? ribbonOrigin(item) : undefined
        }}
        hint={
          <div className="space-y-1 text-xs text-gray-500 dark:text-zinc-400">
            {canEdit ? (
              <p>Удаление снимает привязку к товару; публичный файл остаётся на месте.</p>
            ) : (
              <p>Набор кабинета показан как есть — правка отсюда недоступна.</p>
            )}
            {canEdit && photos.length === 1 && (
              <p className="text-amber-700 dark:text-amber-300">{LAST_PHOTO_TEXT}</p>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3 dark:border-zinc-800">
        <Button
          size="sm"
          icon={<Save className="h-4 w-4" />}
          disabled={!canEdit || !dirtyCabinets.has(activeCabinet) || !photos.length}
          loading={saving}
          onClick={requestSave}
        >
          Сохранить набор
        </Button>
        <Select value={copyTarget} onValueChange={setCopyTarget} disabled={!targetOptions.length}>
          {targetOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
        <Button
          size="sm"
          variant="secondary"
          icon={<Copy className="h-4 w-4" />}
          disabled={!canEdit || !copyTarget || !photos.length || copyUnavailable}
          loading={copying}
          title={copyUnavailable ? 'Копирование доступно только для файлов, загруженных в текущей сессии' : undefined}
          onClick={() => void copy()}
        >
          Скопировать
        </Button>
        {savedSite && (
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="h-4 w-4" />}
            disabled={!canRebuildSite}
            loading={rebuilding}
            title={!canRebuildSite ? 'Не хватает права Site:UPDATE' : undefined}
            onClick={() => void rebuildSite()}
          >
            Пересобрать карточку сайта
          </Button>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        layer="nested"
        title="Изменить набор визуалов товара с макетом"
        message={`${PIPELINE_WARNING}${
          affectedCount !== null ? `\nЗатронет: ${affectedCardsText(affectedCount)} кабинета «${activeLabel}».` : ''
        }`}
        confirmLabel="Сохранить набор"
        loading={saving}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          void save()
        }}
      />
    </div>
  )
}

export default ProductMediaManager
